// Transparency module types — community improvement projects

export enum TransparencyProjectCategory {
  MAINTENANCE = 'MAINTENANCE',
  SECURITY = 'SECURITY',
  REMODELING = 'REMODELING',
  LANDSCAPING = 'LANDSCAPING',
  INFRASTRUCTURE = 'INFRASTRUCTURE',
  AMENITIES = 'AMENITIES',
  TECHNOLOGY = 'TECHNOLOGY',
  OTHER = 'OTHER',
}

export enum TransparencyProjectPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum TransparencyProjectStatus {
  PLANNING = 'PLANNING',
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS',
  PAUSED = 'PAUSED',
  DELAYED = 'DELAYED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export type TransparencyProjectVisibility = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

/** Pestaña de la lista admin — distinta del estado operativo del proyecto. */
export type TransparencyProjectView = 'all' | 'active' | 'completed' | 'archived';

export interface TransparencyProjectCounts {
  comments: number;
  questions: number;
  images: number;
  documents: number;
}

export interface TransparencyDashboardStats {
  total: number;
  byStatus: Record<string, number>;
  completionRate: number;
  averageProgress: number;
}

export interface TransparencyProject {
  id: string;
  title: string;
  shortDescription?: string | null;
  description: string;
  category: TransparencyProjectCategory;
  priority: TransparencyProjectPriority;
  status: TransparencyProjectStatus;
  visibility: TransparencyProjectVisibility;
  progress: number;
  startDate?: string | null;
  estimatedCompletionDate?: string | null;
  actualCompletionDate?: string | null;
  responsiblePerson?: string | null;
  communityLocation?: string | null;
  budget?: string | null;
  finalCost?: string | null;
  coverImageFileId?: string | null;
  coverImageUrl?: string | null;
  commentsEnabled: boolean;
  questionsEnabled: boolean;
  reactionsEnabled: boolean;
  /** Denormalizado de la última entrada de bitácora — alimenta la columna "Última actividad". */
  lastActivityAt?: string | null;
  lastActivityTitle?: string | null;
  /** Participación y media agregadas por el backend en la misma query de la lista. */
  counts?: TransparencyProjectCounts;
  /** Secciones a las que aplica (§6.5). Vacío u omitido = todo el residencial. */
  sectionIds?: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTransparencyProjectDTO {
  title: string;
  shortDescription?: string;
  description: string;
  category?: TransparencyProjectCategory;
  priority?: TransparencyProjectPriority;
  status?: TransparencyProjectStatus;
  // `progress` ausente a propósito: el avance solo se mueve publicando una
  // actualización de bitácora (addTimelineUpdate), nunca editando el proyecto.
  startDate?: string;
  estimatedCompletionDate?: string;
  actualCompletionDate?: string;
  responsiblePerson?: string;
  communityLocation?: string;
  budget?: string;
  coverImageFileId?: string;
  commentsEnabled?: boolean;
  questionsEnabled?: boolean;
  /** Secciones a las que aplica (§6.5). Vacío u omitido = todo el residencial. */
  sectionIds?: string[];
}

export interface UpdateTransparencyProjectDTO extends Partial<CreateTransparencyProjectDTO> {
  finalCost?: string;
}

export type TransparencyImageKind = 'NONE' | 'BEFORE' | 'AFTER';

export interface TransparencyProjectImage {
  id: string;
  projectId: string;
  fileId: string;
  url?: string | null;
  isBeforeAfter: TransparencyImageKind;
  displayOrder: number;
  /** Cuando viene seteado, la foto se publicó dentro de esa actualización de bitácora. */
  updateId?: string | null;
  createdAt: string;
}

export interface TransparencyProjectDocument {
  id: string;
  projectId: string;
  fileId: string;
  url?: string | null;
  label?: string | null;
  updateId?: string | null;
  createdAt: string;
}

export interface TransparencyTimelineUpdate {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  progressChange?: number | null;
  statusChange?: TransparencyProjectStatus | null;
  authorId: string;
  images: TransparencyProjectImage[];
  documents: TransparencyProjectDocument[];
  createdAt: string;
}

export interface CreateTimelineUpdateDTO {
  title: string;
  description?: string;
  progressChange?: number;
  statusChange?: TransparencyProjectStatus;
  imageFileIds?: string[];
  documentFileIds?: string[];
}

export interface AddProjectImageDTO {
  fileId: string;
  isBeforeAfter?: TransparencyImageKind;
  displayOrder?: number;
}

export interface AddProjectDocumentDTO {
  fileId: string;
  label?: string;
}

export type TransparencyCommentType = 'COMMENT' | 'QUESTION';

export interface TransparencyComment {
  id: string;
  projectId: string;
  type: TransparencyCommentType;
  userId: string;
  userName: string;
  text: string;
  isAdmin: boolean;
  createdAt: string;
}

export interface CreateTransparencyCommentDTO {
  type: TransparencyCommentType;
  text: string;
}
